// samplingEventPatchQueue.js
// IndexedDB queue management for Sampling Event form autosave and holding websocket requests for offline capabilities

import { openDB } from "idb";
import { withIdbRetry } from "./idbRetry";

const DB_NAME = "samplingEventPatchQueue";
const STORE_NAME = "patches";

const getDb = async () => {
  return openDB(DB_NAME, 1, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, {
          keyPath: "queueId",
          autoIncrement: true,
        });
        store.createIndex("eventId", "eventId", { unique: false });
      }
    },
  });
};

export const queuePatch = async (eventId, patch) => {
  return withIdbRetry(
    async () => {
      const db = await getDb();
      return db.add(STORE_NAME, {
        eventId,
        patch,
        timestamp: Date.now(),
      });
    },
    undefined,
    "samplingEventPatchQueue.queuePatch"
  );
};

export const getQueuedPatches = async (eventId) => {
  return withIdbRetry(
    async () => {
      const db = await getDb();
      const tx = db.transaction(STORE_NAME, "readonly");
      const index = tx.store.index("eventId");
      return index.getAll(eventId);
    },
    [],
    "samplingEventPatchQueue.getQueuedPatches"
  );
};

export const removePatchFromQueue = async (queueId) => {
  return withIdbRetry(
    async () => {
      const db = await getDb();
      await db.delete(STORE_NAME, queueId);
    },
    undefined,
    "samplingEventPatchQueue.removePatchFromQueue"
  );
};

export const getPendingCount = async (eventId) => {
  const patches = await getQueuedPatches(eventId);
  return patches.length;
};

export const flushQueue = async (eventId, emitFn) => {
  const patches = await getQueuedPatches(eventId);
  let successCount = 0;
  for (const item of patches) {
    const result = await emitFn(eventId, item.patch);
    // Remove from queue if: success OR server rejected it (validation error)
    // Only keep in queue if it should be retried (network issues)
    if (!result.error || !result.shouldQueue) {
      await removePatchFromQueue(item.queueId);
      if (!result.error) {
        successCount++;
      }
    }
  }
  return successCount;
};

export const clearQueue = async (eventId) => {
  return withIdbRetry(
    async () => {
      const db = await getDb();
      const tx = db.transaction(STORE_NAME, "readwrite");
      const index = tx.store.index("eventId");
      const keys = await index.getAllKeys(eventId);
      for (const key of keys) {
        await tx.store.delete(key);
      }
      await tx.done;
    },
    undefined,
    "samplingEventPatchQueue.clearQueue"
  );
};
